import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';
import SynletsLogoSvg from 'assets/images/synlets-logo.svg';
import { useNavTheme } from 'contexts/NavThemeContext';

// Breathe animation — logo gently scales and glows
const breathe = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(0.96); filter: brightness(0.9); }
  50% { opacity: 1; transform: scale(1); filter: brightness(1.15); }
`;

// Subtle shimmer for the loading text
const textPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.85; }
`;

// Orbital ring animation
const orbitSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

interface SynletsLoaderProps {
  message?: string;
  /** 'page' = centered within main content, 'fullscreen' = centered in full viewport, 'section' = inline */
  variant?: 'page' | 'fullscreen' | 'section';
}

export default function SynletsLoader({ message, variant = 'page' }: SynletsLoaderProps) {
  const { mode } = useNavTheme();
  const isLight = mode === 'light';
  const isLarge = variant === 'page' || variant === 'fullscreen';
  const logoSize = isLarge ? 56 : 36;
  const orbitSize = isLarge ? 96 : 64;

  // Theme-aware colors
  const ring1Color = isLight ? '#4f46e5' : '#6366f1';
  const ring1Fade = isLight ? 'rgba(79, 70, 229, 0.35)' : 'rgba(99, 102, 241, 0.3)';
  const ring2Color = isLight ? '#6366f1' : '#818cf8';
  const ring2Fade = isLight ? 'rgba(99, 102, 241, 0.25)' : 'rgba(129, 140, 248, 0.2)';
  const textColor = isLight ? 'rgba(71, 85, 105, 0.75)' : 'rgba(148, 163, 184, 0.8)';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: variant === 'fullscreen' ? '100vh' : variant === 'page' ? 'calc(100vh - 200px)' : 300,
        gap: 2.5,
        userSelect: 'none',
      }}
    >
      {/* Logo container with orbital ring */}
      <Box
        sx={{
          position: 'relative',
          width: orbitSize,
          height: orbitSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Orbital ring */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `1.5px solid transparent`,
            borderTopColor: ring1Color,
            borderRightColor: ring1Fade,
            animation: `${orbitSpin} 2s linear infinite`,
          }}
        />
        {/* Second ring — counter-rotate, offset */}
        <Box
          sx={{
            position: 'absolute',
            inset: 4,
            borderRadius: '50%',
            border: `1px solid transparent`,
            borderBottomColor: ring2Color,
            borderLeftColor: ring2Fade,
            animation: `${orbitSpin} 3s linear infinite reverse`,
          }}
        />

        {/* Logo */}
        <Box
          component="img"
          src={SynletsLogoSvg}
          alt=""
          sx={{
            width: logoSize,
            height: logoSize,
            objectFit: 'contain',
            animation: `${breathe} 2.4s ease-in-out infinite`,
          }}
        />
      </Box>

      {/* Loading text */}
      {message && (
        <Typography
          sx={{
            fontSize: isLarge ? '0.9rem' : '0.82rem',
            fontWeight: 500,
            fontFamily: '"Satoshi", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            letterSpacing: '0.03em',
            color: textColor,
            animation: `${textPulse} 2.4s ease-in-out infinite`,
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
}
